import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Divider, Chip, Alert, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { evcpdAmcApi } from '../../../api/evcpdAmc.api';
import { extractApiError } from '../../../api/error';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  contractId: number | null;
  onClose: () => void;
  onChanged: () => void;
}

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function AmcContractDetailDialog({ contractId, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = contractId !== null;
  const id = contractId ?? 0;
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [renewStart, setRenewStart] = useState('');
  const [renewEnd, setRenewEnd] = useState('');

  const { data: c } = useQuery({ queryKey: ['amc-contract', id], queryFn: () => evcpdAmcApi.get(id), enabled: open });
  const refresh = () => { setError(null); qc.invalidateQueries({ queryKey: ['amc-contract', id] }); onChanged(); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const submitMut = useMutation({ mutationFn: () => evcpdAmcApi.submit(id), onSuccess: refresh, onError: onErr });
  const approveMut = useMutation({ mutationFn: () => evcpdAmcApi.approve(id), onSuccess: refresh, onError: onErr });
  const rejectMut = useMutation({ mutationFn: () => evcpdAmcApi.reject(id, rejectReason), onSuccess: refresh, onError: onErr });
  const renewMut = useMutation({ mutationFn: () => evcpdAmcApi.renew(id, { amcStartDate: renewStart, amcEndDate: renewEnd }), onSuccess: refresh, onError: onErr });

  if (!c) return null;
  const s = c.status;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <span>{c.amcCode}</span>
          <StatusBadge status={c.effectiveStatus} />
          <Chip size="small" label={c.contractType.replace(/_/g, ' ')} />
          <Chip size="small" label={`PM: ${c.pmFrequency.replace(/_/g, ' ')}`} />
          {c.daysToExpiry !== null && c.daysToExpiry <= 30 && c.daysToExpiry >= 0 && <Chip size="small" color="warning" label={`expires in ${c.daysToExpiry}d`} />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {c.rejectionReason && <Alert severity="warning" sx={{ mb: 2 }}>Rejected: {c.rejectionReason}</Alert>}
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Value</Typography><Typography>{inr(c.contractValue)}</Typography></Grid>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">AMC Period</Typography><Typography>{c.amcStartDate} → {c.amcEndDate}</Typography></Grid>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Warranty</Typography><Typography>{c.warrantyEndDate ?? '—'}</Typography></Grid>
          <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">OEM / Serial</Typography><Typography>{c.oem ?? '—'} / {c.serialNumber ?? '—'}</Typography></Grid>
        </Grid>

        <Divider textAlign="left" sx={{ mb: 1 }}>PM Schedule</Divider>
        <Table size="small">
          <TableHead><TableRow><TableCell>#</TableCell><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell>Ticket</TableCell></TableRow></TableHead>
          <TableBody>
            {(c.schedules ?? []).map((sch) => (
              <TableRow key={sch.id}>
                <TableCell>{sch.sequenceNo}</TableCell>
                <TableCell>{sch.scheduledDate}</TableCell>
                <TableCell><Chip size="small" label={sch.status.replace(/_/g, ' ')} color={sch.status === 'completed' ? 'success' : sch.status === 'missed' ? 'error' : 'default'} /></TableCell>
                <TableCell>{sch.ticketId ? `#${sch.ticketId}` : '—'}</TableCell>
              </TableRow>
            ))}
            {(c.schedules ?? []).length === 0 && <TableRow><TableCell colSpan={4}>No PM schedule yet (generated on approval).</TableCell></TableRow>}
          </TableBody>
        </Table>

        {s === 'pending_approval' && hasPermission('evcpd_amc_contract.approve') && (
          <TextField size="small" label="Rejection reason (to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} fullWidth sx={{ mt: 2 }} />
        )}
        {['active', 'expiring', 'expired'].includes(s) && hasPermission('evcpd_amc_contract.renew') && !c.renewedToId && (
          <Stack direction="row" spacing={2} mt={2}>
            <TextField size="small" label="Renewal start" type="date" InputLabelProps={{ shrink: true }} value={renewStart} onChange={(e) => setRenewStart(e.target.value)} />
            <TextField size="small" label="Renewal end" type="date" InputLabelProps={{ shrink: true }} value={renewEnd} onChange={(e) => setRenewEnd(e.target.value)} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {(s === 'draft' || s === 'rejected') && hasPermission('evcpd_amc_contract.update') && <Button variant="contained" onClick={() => submitMut.mutate()} disabled={submitMut.isPending}>Submit for Approval</Button>}
        {s === 'pending_approval' && hasPermission('evcpd_amc_contract.approve') && (
          <>
            <Button color="success" variant="contained" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>Approve</Button>
            <Button color="error" variant="outlined" disabled={!rejectReason || rejectMut.isPending} onClick={() => rejectMut.mutate()}>Reject</Button>
          </>
        )}
        {['active', 'expiring', 'expired'].includes(s) && hasPermission('evcpd_amc_contract.renew') && !c.renewedToId && (
          <Button variant="outlined" disabled={!renewStart || !renewEnd || renewMut.isPending} onClick={() => renewMut.mutate()}>Renew</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
